/**
 * ReportResultPane — table / chart / both over a Custom Report Engine envelope.
 *
 * Chart mapping lives in @busmgr/report-chart (no recharts there). This file is
 * the per-app recharts renderer. Keep in sync with
 * site_back_office/src/components/views/report-forms/ReportResultPane.js.
 *
 * Specs without a resolvable chart hint stay table-only (no toggle chrome).
 * Hierarchy / header-detail control columns and non-numeric measures skip.
 */

import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildChartModel,
  resolveChartHint,
} from '@busmgr/report-chart';
import ReportResultTable from './ReportResultTable';

const SERIES_COLORS = [
  'var(--primary, #16a34a)',
  '#2563eb',
  '#d97706',
  '#7c3aed',
  '#db2777',
  '#0891b2',
  '#4d7c0f',
  '#c2410c',
];

const fmtMeasure = (value, type) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (type === 'currency') {
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (type === 'integer') return Math.round(n).toLocaleString('en-US');
  return n.toLocaleString('en-US');
};

const axisTick = (value, type) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (type === 'currency') {
    return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  return n.toLocaleString('en-US');
};

function ReportResultChart({ model }) {
  if (!model || !model.data || model.data.length === 0) return null;

  const ChartTag = model.type === 'line' ? LineChart : model.type === 'area' ? AreaChart : BarChart;
  const showLegend = model.seriesKeys.length > 1;

  return (
    <div style={{ width: '100%', height: 360, marginBottom: '16px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <ChartTag data={model.data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid stroke="var(--border, #e5e7eb)" strokeDasharray="3 3" />
          <XAxis
            dataKey={model.categoryKey}
            tick={{ fontSize: 12, fill: 'var(--text-muted, #6b7280)' }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 12, fill: 'var(--text-muted, #6b7280)' }}
            tickFormatter={(v) => axisTick(v, model.measureType)}
            width={72}
          />
          <Tooltip
            formatter={(value, name) => [fmtMeasure(value, model.measureType), name]}
            labelFormatter={(label) => `${model.categoryLabel}: ${label}`}
          />
          {showLegend && <Legend />}
          {model.seriesKeys.map((key, i) => {
            const color = SERIES_COLORS[i % SERIES_COLORS.length];
            if (model.type === 'line') {
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  dot={{ r: 3 }}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              );
            }
            if (model.type === 'area') {
              return (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  fill={color}
                  fillOpacity={0.2}
                  isAnimationActive={false}
                />
              );
            }
            return (
              <Bar
                key={key}
                dataKey={key}
                fill={color}
                isAnimationActive={false}
              />
            );
          })}
        </ChartTag>
      </ResponsiveContainer>
    </div>
  );
}

const VIEWS = [
  { id: 'table', label: 'Table' },
  { id: 'chart', label: 'Chart' },
  { id: 'both', label: 'Both' },
];

const ReportResultPane = ({ result, specChart, typeDefaultChart }) => {
  const hint = useMemo(
    () => resolveChartHint(specChart, typeDefaultChart),
    [specChart, typeDefaultChart]
  );
  const model = useMemo(
    () => buildChartModel(hint, result),
    [hint, result]
  );
  const [view, setView] = useState('both');
  const showChart = !!model && (view === 'chart' || view === 'both');
  const showTable = !model || view === 'table' || view === 'both';

  return (
    <div>
      {model && (
        <div
          role="tablist"
          aria-label="Report view"
          style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}
        >
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={view === v.id}
              className={view === v.id ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => setView(v.id)}
              style={{ padding: '6px 12px', fontSize: '13px' }}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}
      {showChart && <ReportResultChart model={model} />}
      {showTable && <ReportResultTable result={result} />}
    </div>
  );
};

export default ReportResultPane;
